// Insurance (BIA) tab. Two stacked sections:
//   1. Document requests panel — open requests, pending response, with
//      status-change actions and a "Request current policies" CTA.
//   2. Live policies — every policy on file for this household, with
//      product / carrier / lifecycle / premium chips.
//
// Phase C port of the legacy modal's BIATab. Re-uses the existing
// insuranceApi helpers + RequestDocsModal so the request workflow
// stays bit-for-bit identical to the legacy modal.

import { useEffect, useState } from 'react';
import { fetchPolicies, fetchRequests, updateRequest } from '../../../utils/insuranceApi';
import { formatCurrency } from '../../../utils/formatting';
import RequestDocsModal from '../../Insurance/RequestDocsModal';
import { EmptyState, AddServiceCTA } from './_engagementsList';

const PRODUCT_LABELS = {
  auto: 'Auto', home: 'Home', umbrella: 'Umbrella',
  life: 'Life', disability: 'Disability',
  watercraft: 'Watercraft', wind: 'Wind', flood: 'Flood',
};

const LIFECYCLE_BADGES = {
  in_force:         { label: 'In Force',         color: 'bg-deep-teal/15 text-deep-teal' },
  future_effective: { label: 'Future Effective', color: 'bg-amber-100 text-amber-700' },
  expired:          { label: 'Expired',          color: 'bg-rose/15 text-rose' },
  cancelled:        { label: 'Cancelled',        color: 'bg-gray-200 text-gray-600' },
  lapsed:           { label: 'Lapsed',           color: 'bg-amber-100 text-amber-700' },
};

const REQUEST_STATUS_COLORS = {
  pending:   'bg-amber-100 text-amber-700',
  sent:      'bg-bullogic-blue/15 text-bullogic-blue',
  received:  'bg-deep-teal/15 text-deep-teal',
  closed:    'bg-gray-200 text-gray-600',
  cancelled: 'bg-rose/15 text-rose',
};

export default function InsuranceTab({ household, members }) {
  const [policies, setPolicies] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [requestModalOpen, setRequestModalOpen] = useState(false);

  // BIA engagement membership — separate from policies on file. A
  // household can have policies before being added as a BIA client or
  // vice versa.
  const biaEngagements = members.filter(m => m.division === 'bia');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      fetchPolicies({ household_id: household.id }).catch(() => []),
      fetchRequests({ household_id: household.id }).catch(() => []),
    ]).then(([pols, reqs]) => {
      if (cancelled) return;
      setPolicies(pols || []);
      setRequests(reqs || []);
      setLoading(false);
    }).catch(e => {
      if (cancelled) return;
      setError(e.message);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [household.id, reloadTick]);

  async function handleRequestStatusChange(req, newStatus) {
    try {
      await updateRequest(req.id, { status: newStatus });
      setReloadTick(t => t + 1);
    } catch (e) {
      alert(`Update failed: ${e.message}`);
    }
  }

  const inForcePolicies = policies.filter(p => p.lifecycle === 'in_force');
  const annualPremium = inForcePolicies.reduce((s, p) => s + (Number(p.premium_annual) || 0), 0);
  const openRequests = requests.filter(r => r.status === 'pending' || r.status === 'sent');
  const recentClosed = requests
    .filter(r => r.status === 'received' || r.status === 'closed')
    .slice(0, 3);

  return (
    <div className="space-y-4">
      {/* Summary card */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
        <div className="flex items-baseline justify-between mb-2">
          <div>
            <h2 className="text-[15px] font-bold text-navy">Insurance (BIA)</h2>
            <p className="text-[11px] text-[#888] mt-0.5">
              {policies.length} {policies.length === 1 ? 'policy' : 'policies'} on file ·
              {' '}{inForcePolicies.length} in force ·
              {' '}{formatCurrency(annualPremium)} annual premium
            </p>
          </div>
          {biaEngagements.length === 0 && (
            <AddServiceCTA division="bia" label="+ Add Insurance Service" />
          )}
        </div>
        {biaEngagements.length > 0 && (
          <p className="text-[11px] text-[#666] mt-2">
            BIA service engaged · {biaEngagements.length} engagement{biaEngagements.length === 1 ? '' : 's'}
          </p>
        )}
      </div>

      {/* Document requests */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-[13px] font-bold text-navy">Document Requests</h3>
            <p className="text-[11px] text-[#888] mt-0.5">
              {openRequests.length === 0
                ? 'No open requests.'
                : `${openRequests.length} open request${openRequests.length === 1 ? '' : 's'}`}
            </p>
          </div>
          <button
            onClick={() => setRequestModalOpen(true)}
            className="text-[11px] font-bold text-white bg-bullogic-blue px-3 py-1.5 rounded-[3px] hover:bg-bullogic-blue/90 transition-colors"
          >
            + Request Current Policies
          </button>
        </div>

        {openRequests.length > 0 && (
          <div className="space-y-1.5 mb-3">
            {openRequests.map(r => (
              <RequestRow key={r.id} request={r} onChangeStatus={handleRequestStatusChange} />
            ))}
          </div>
        )}

        {recentClosed.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="text-[10px] uppercase tracking-wider text-[#888] font-bold mb-2">
              Recently Received
            </div>
            <div className="space-y-1">
              {recentClosed.map(r => (
                <RequestRow key={r.id} request={r} onChangeStatus={handleRequestStatusChange} compact />
              ))}
            </div>
          </div>
        )}

        {openRequests.length === 0 && recentClosed.length === 0 && (
          <EmptyState label="No document requests yet for this household." />
        )}
      </div>

      {/* Policies */}
      <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
        <h3 className="text-[13px] font-bold text-navy mb-3">Policies on file</h3>
        {loading ? (
          <p className="text-[12px] text-[#888] italic">Loading policies…</p>
        ) : error ? (
          <p className="text-[12px] text-rose">Error loading policies: {error}</p>
        ) : policies.length === 0 ? (
          <EmptyState label="No insurance policies on file yet. Use the request button above to ask the client to send theirs." />
        ) : (
          <PolicyList policies={policies} />
        )}
      </div>

      {requestModalOpen && (
        <RequestDocsModal
          household={{ id: household.id, name: household.name }}
          onClose={() => setRequestModalOpen(false)}
          onCreated={() => setReloadTick(t => t + 1)}
        />
      )}
    </div>
  );
}

function PolicyList({ policies }) {
  return (
    <div className="space-y-1.5">
      {policies.map(p => {
        const lc = LIFECYCLE_BADGES[p.lifecycle];
        return (
          <div
            key={p.id}
            className="flex items-center justify-between gap-4 bg-bone/30 rounded-[4px] px-3.5 py-2.5 hover:bg-bone/50 transition-colors text-[12px]"
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <span className="font-bold text-navy w-20 shrink-0">
                {PRODUCT_LABELS[p.product_type] || p.product_type}
              </span>
              <span className="text-[#666] truncate">{p.carrier_name || 'unknown carrier'}</span>
              <span className="font-mono text-[10px] text-[#888]">{p.policy_number || '—'}</span>
              {p.we_broker && (
                <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-[2px] bg-bullogic-blue/15 text-bullogic-blue">
                  Broker
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-[11px] text-[#888]">
                {p.effective_date || '?'} → {p.expiration_date || '?'}
              </span>
              <span className="font-bold text-navy">
                {formatCurrency(p.premium_annual || 0)}
                <span className="text-[9px] text-[#aaa] ml-0.5">/yr</span>
              </span>
              {lc && (
                <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-[3px] ${lc.color}`}>
                  {lc.label}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RequestRow({ request, onChangeStatus, compact = false }) {
  const sc = REQUEST_STATUS_COLORS[request.status] || 'bg-gray-100 text-gray-600';
  const requested = request.requested_at ? new Date(request.requested_at).toLocaleDateString() : '?';
  const due = request.due_by ? new Date(request.due_by).toLocaleDateString() : null;
  const overdue = request.due_by
    && (request.status === 'pending' || request.status === 'sent')
    && new Date(request.due_by) < new Date();

  return (
    <div className={`flex items-center justify-between gap-3 bg-white rounded-[3px] px-3 py-2 text-[11px] border border-gray-200 ${compact ? 'opacity-70' : ''}`}>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-[2px] ${sc}`}>
          {request.status}
        </span>
        <span className="text-navy">{(request.request_type || '').replace(/_/g, ' ')}</span>
        <span className="text-[#888]">requested {requested}</span>
        {request.recipient_email && <span className="text-[#888]">→ {request.recipient_email}</span>}
        {due && (
          <span className={overdue ? 'text-rose font-bold' : 'text-[#888]'}>
            due {due}{overdue ? ' (overdue)' : ''}
          </span>
        )}
      </div>
      {!compact && (
        <div className="flex items-center gap-1 shrink-0">
          {request.status === 'pending' && (
            <button onClick={() => onChangeStatus(request, 'sent')} className="text-[10px] font-bold text-bullogic-blue hover:underline px-1">
              Mark Sent
            </button>
          )}
          {(request.status === 'pending' || request.status === 'sent') && (
            <button onClick={() => onChangeStatus(request, 'received')} className="text-[10px] font-bold text-deep-teal hover:underline px-1">
              Mark Received
            </button>
          )}
          {request.status === 'received' && (
            <button onClick={() => onChangeStatus(request, 'closed')} className="text-[10px] font-bold text-[#666] hover:underline px-1">
              Close
            </button>
          )}
          {(request.status === 'pending' || request.status === 'sent') && (
            <button onClick={() => onChangeStatus(request, 'cancelled')} className="text-[10px] font-bold text-rose hover:underline px-1">
              Cancel
            </button>
          )}
        </div>
      )}
    </div>
  );
}
